import React, { useEffect, useRef, useState } from "react";
import { Rewind, Play, Pause } from "lucide-react";

export default function DvrScrubBar({ videoEl, testId }) {
  const [range, setRange] = useState({ start: 0, end: 0, current: 0, paused: false, isLive: false });
  const rafRef = useRef();

  useEffect(() => {
    if (!videoEl) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled || !videoEl) return;
      try {
        const seek = videoEl.seekable;
        if (seek && seek.length > 0) {
          const start = seek.start(0);
          const end = seek.end(seek.length - 1);
          const isLive = !Number.isFinite(videoEl.duration) || videoEl.duration === Infinity || (end - start) > 30;
          setRange({ start, end, current: videoEl.currentTime, paused: videoEl.paused, isLive });
        }
      } catch { /* noop */ }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [videoEl]);

  if (!videoEl || range.end <= range.start) {
    return (
      <div className="font-mono text-[10px] text-zinc-500 italic px-2 py-2" data-testid={testId ? `${testId}-empty` : undefined}>
        DVR not available for this stream
      </div>
    );
  }
  const span = range.end - range.start;
  const pos = (range.current - range.start) / span;
  const behindLive = Math.max(0, range.end - range.current);
  const seekTo = (fraction) => {
    try { videoEl.currentTime = range.start + Math.max(0, Math.min(1, fraction)) * span; } catch { /* noop */ }
  };
  const jumpBack = (sec) => {
    try { videoEl.currentTime = Math.max(range.start, videoEl.currentTime - sec); } catch { /* noop */ }
  };
  const goLive = () => {
    try { videoEl.currentTime = range.end - 1; } catch { /* noop */ }
    try { videoEl.play(); } catch { /* noop */ }
  };
  const togglePlay = () => {
    if (videoEl.paused) { try { videoEl.play(); } catch { /* noop */ } }
    else { try { videoEl.pause(); } catch { /* noop */ } }
  };
  const fmtHMS = (s) => {
    if (!Number.isFinite(s)) return "00:00";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = Math.floor(s % 60);
    return h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };
  return (
    <div className="p-2 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-mono">
        <button onClick={togglePlay} className="p-1 rounded-sm border border-zinc-800 hover:bg-zinc-900" data-testid={testId ? `${testId}-playpause` : undefined}>
          {range.paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
        </button>
        <button onClick={() => jumpBack(10)} className="p-1 rounded-sm border border-zinc-800 hover:bg-zinc-900" title="Back 10s" data-testid={testId ? `${testId}-back10` : undefined}>
          <Rewind className="w-3 h-3" />
        </button>
        <button onClick={() => jumpBack(60)} className="px-1.5 rounded-sm border border-zinc-800 hover:bg-zinc-900 text-[10px]" data-testid={testId ? `${testId}-back60` : undefined}>-1m</button>
        <button
          onClick={goLive}
          className={`px-1.5 py-0.5 rounded-sm border ${behindLive < 2 ? "border-red-500 text-red-400" : "border-zinc-800 text-zinc-300 hover:bg-zinc-900"}`}
          data-testid={testId ? `${testId}-live` : undefined}
        >
          {behindLive < 2 ? "● LIVE" : "GO LIVE"}
        </button>
        <span className="ml-auto text-zinc-500">
          -{fmtHMS(behindLive)} · window {fmtHMS(span)}
        </span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.001"
        value={pos}
        onChange={(e) => seekTo(Number(e.target.value))}
        className="w-full accent-red-500 cursor-pointer"
        data-testid={testId ? `${testId}-slider` : undefined}
      />
    </div>
  );
}
